import { useMemo, useState } from "react";
import type { Trail } from "@hash/shared";
import {
  displayHares,
  eventLabel,
  isSpecialEvent,
  isWithinCheckInWindow,
} from "@hash/shared";
import { api } from "../api/client";
import { formatMoney } from "../lib/checkInStatus";
import { formatTrailDate, localDate } from "../lib/trailDate";

interface Props {
  trails: Trail[];
  isAdmin: boolean;
  onSelectTrail: (trail: Trail) => void;
  onCreateTrail: () => void;
  // Start a new special event (Milestone 9).
  onCreateEvent: () => void;
  // `renumbered` is how many later trails were shifted down a number to close
  // the gap (deleting a future trail). When > 0 the caller must refetch, since
  // the surviving rows' numbers and ids are now stale in local state.
  onTrailDeleted: (trailId: string, renumbered: number) => void;
}

type Tab = "upcoming" | "past";

/**
 * Post-login landing for admin / misman / hare, with a primary CTA to start a
 * new trail. Trails split into two tabs: Upcoming (today onward, soonest
 * first — the default) and Past (newest first, for back-filling attendance).
 * A hare whose check-in window is open skips the picker via `main.tsx`.
 */
export function TrailPicker({
  trails,
  isAdmin,
  onSelectTrail,
  onCreateTrail,
  onCreateEvent,
  onTrailDeleted,
}: Props) {
  const [tab, setTab] = useState<Tab>("upcoming");
  // Trails fetched by "Load more" beyond the window `trails` was seeded with,
  // merged into the seed below. `hasMore` tracks, per direction, whether the
  // server still has pages past what we hold; we assume yes until a page tells
  // us otherwise. `loadingMore` guards against double-fetches.
  const [extra, setExtra] = useState<Trail[]>([]);
  const [hasMore, setHasMore] = useState({ upcoming: true, past: true });
  const [loadingMore, setLoadingMore] = useState(false);
  // Optional kennel filter ("" = all kennels). Filters by the immutable
  // kennelId; options are derived from the loaded trails below. Note this only
  // narrows trails already loaded — "Load more" still pages the full window.
  const [kennelId, setKennelId] = useState<string>("");
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Per-trail self check-in confirmation (Story 13.1): trailId -> owed message.
  const [selfDone, setSelfDone] = useState<Record<string, string>>({});

  const selfCheckIn = async (t: Trail) => {
    try {
      const { owed } = await api.me.checkIn(t.id);
      setSelfDone((s) => ({
        ...s,
        [t.id]:
          owed > 0
            ? `Checked in — you owe ${formatMoney(owed)} (cash to the hare).`
            : "Checked in — you're covered.",
      }));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Self check-in failed");
    }
  };

  // The seed (`trails`) plus everything Load more has pulled in, deduped by id.
  const all = useMemo(() => {
    const byId = new Map<string, Trail>();
    for (const t of trails) byId.set(t.id, t);
    for (const t of extra) byId.set(t.id, t);
    return [...byId.values()];
  }, [trails, extra]);

  // Kennels present in the loaded trails, unique by id and sorted by display
  // name — the filter dropdown's options. Drawn from all loaded trails (both
  // tabs) so the choices stay stable when switching Upcoming/Past.
  const kennels = Array.from(
    new Map(all.map((t) => [t.kennelId, t.kennel])).entries()
  )
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name));

  const today = localDate();
  // The active tab's trails, sorted but not kennel-filtered — this drives the
  // "Load more" cursor so an active filter can't corrupt keyset paging.
  const inTab =
    tab === "upcoming"
      ? all
          .filter((t) => t.date >= today)
          .sort((a, b) => a.date.localeCompare(b.date))
      : all
          .filter((t) => t.date < today)
          .sort((a, b) => b.date.localeCompare(a.date));
  const shown = inTab.filter((t) => !kennelId || t.kennelId === kennelId);

  // Fetch the next page in the active tab's direction, resuming after the last
  // trail we hold (unfiltered) so the server never re-sends what we hold.
  const loadMore = async () => {
    setLoadingMore(true);
    setError(null);
    try {
      const last = inTab[inTab.length - 1];
      const { trails: more, hasMore: stillMore } = await api.trails.listPage({
        direction: tab,
        after: last ? { id: last.id, date: last.date } : undefined,
      });
      setExtra((prev) => {
        const byId = new Map(prev.map((t) => [t.id, t]));
        for (const t of more) byId.set(t.id, t);
        return [...byId.values()];
      });
      setHasMore((h) => ({ ...h, [tab]: stillMore }));
    } catch (e) {
      setError(extractError(e, "Failed to load more trails"));
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <div className="min-h-screen pb-24">
      <div className="px-4 pt-4 space-y-2">
        <button
          onClick={onCreateTrail}
          className="w-full h-12 rounded-lg bg-slate-900 text-white font-medium active:bg-slate-700"
        >
          + Start a new trail
        </button>
        <button
          onClick={onCreateEvent}
          className="w-full h-12 rounded-lg border border-slate-300 text-slate-700 font-medium active:bg-slate-50"
        >
          + Special event
        </button>
      </div>

      {error && (
        <p className="px-4 pt-3 text-sm text-red-600">{error}</p>
      )}

      <section className="mt-6">
        <div
          role="tablist"
          aria-label="Trails"
          className="flex border-b border-slate-200 px-4"
        >
          {(
            [
              ["upcoming", "Upcoming"],
              ["past", "Past"],
            ] as const
          ).map(([key, label]) => (
            <button
              key={key}
              role="tab"
              aria-selected={tab === key}
              onClick={() => setTab(key)}
              className={`min-h-[44px] px-4 text-sm font-medium border-b-2 -mb-px ${
                tab === key
                  ? "border-slate-900 text-slate-900"
                  : "border-transparent text-slate-500"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        {kennels.length > 1 && (
          <div className="px-4 pt-3">
            <label className="flex flex-col text-xs font-medium text-slate-500">
              Kennel
              <select
                value={kennelId}
                onChange={(e) => setKennelId(e.target.value)}
                className="mt-1 h-11 rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-900"
              >
                <option value="">All kennels</option>
                {kennels.map((k) => (
                  <option key={k.id} value={k.id}>
                    {k.name}
                  </option>
                ))}
              </select>
            </label>
          </div>
        )}
        {shown.length === 0 ? (
          <p className="px-4 py-6 text-sm text-slate-500">
            {tab === "upcoming"
              ? `No upcoming trails in the next 60 days${kennelId ? " for this kennel" : ""}.`
              : `No past trails in the last 60 days${kennelId ? " for this kennel" : ""}.`}
          </p>
        ) : (
          <ul className="divide-y divide-slate-200 bg-white">
            {shown.map((t) => (
              <li key={t.id} className="flex items-stretch">
                <button
                  onClick={() => onSelectTrail(t)}
                  className="flex-1 text-left px-4 py-3 active:bg-slate-50"
                >
                  <div className="font-medium text-slate-900">
                    {t.kennel} {eventLabel(t)}
                    {isSpecialEvent(t) && (
                      <span className="ml-2 align-middle rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                        Event
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-slate-500">
                    {formatTrailDate(t)} · {t.startLocation}
                  </div>
                  {(() => {
                    const hares = displayHares(t);
                    return hares.length ? (
                      <div className="text-sm text-slate-500 break-words">
                        Hares: {hares.join(", ")}
                      </div>
                    ) : (
                      <div className="text-sm text-amber-700 truncate">
                        Hares: TBD
                      </div>
                    );
                  })()}
                  {selfDone[t.id] && (
                    <div className="mt-1 text-sm text-emerald-700">
                      {selfDone[t.id]}
                    </div>
                  )}
                </button>
                {isWithinCheckInWindow(t, Date.now()) && !selfDone[t.id] && (
                  <button
                    onClick={() => selfCheckIn(t)}
                    className="px-3 text-sm font-medium text-slate-700 active:bg-slate-50"
                  >
                    Check me in
                  </button>
                )}
                {isAdmin && (
                  <button
                    onClick={async () => {
                      if (
                        !confirm(
                          `Delete ${t.kennel} ${eventLabel(t)}? This also removes all attendance for it.`
                        )
                      ) {
                        return;
                      }
                      setDeletingId(t.id);
                      setError(null);
                      try {
                        const { renumbered } = await api.trails.delete(t.id);
                        // Drop it from any pages Load more pulled in; the parent
                        // handles the seed (and refetches when a renumber shifted
                        // the surviving trails' ids).
                        setExtra((prev) => prev.filter((x) => x.id !== t.id));
                        onTrailDeleted(t.id, renumbered);
                      } catch (e) {
                        setError(
                          extractError(e, "Failed to delete trail")
                        );
                      } finally {
                        setDeletingId(null);
                      }
                    }}
                    disabled={deletingId === t.id}
                    aria-label="Delete trail"
                    className="w-12 flex items-center justify-center text-slate-400 hover:text-red-600 active:bg-red-50 disabled:opacity-50"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                      <path d="M10 11v6" />
                      <path d="M14 11v6" />
                      <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
                    </svg>
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
        {hasMore[tab] && (
          <div className="px-4 py-4">
            <button
              onClick={loadMore}
              disabled={loadingMore}
              className="w-full h-12 rounded-lg border border-slate-300 text-slate-700 font-medium active:bg-slate-50 disabled:opacity-50"
            >
              {loadingMore ? "Loading…" : "Load more"}
            </button>
          </div>
        )}
      </section>
    </div>
  );
}

function extractError(err: unknown, fallback: string): string {
  if (!(err instanceof Error)) return fallback;
  const match = err.message.match(/^\d+:\s*(.*)$/);
  const body = match ? match[1] : err.message;
  try {
    const parsed = JSON.parse(body);
    if (typeof parsed?.error === "string") return parsed.error;
  } catch {
    // fall through
  }
  return body || fallback;
}
